/**
 * useGlowTutorial — abandons the glow tutorial if the user clicks
 * anywhere outside the currently glowed element ([data-glow-target]).
 */

import { useEffect } from 'react';
import { useUiStore } from '@/store/uiStore.ts';
import { useTutorialStore, selectExampleTourActive } from '@/store/tutorialStore.ts';

export function useGlowTutorial() {
  const step = useUiStore(s => s.glowTutorialStep);
  const setStep = useUiStore(s => s.setGlowTutorialStep);
  // 20260921 — EXAMPLE TOUR ONLY (design §2.1). `glowTutorialStep` is the flag the Getting started
  // render beat parks ('enhance') and its rail owns; the characters rail never touches it — its
  // spotlight is the separate guideGlow ATTRIBUTE (utils/guideGlow). So in a characters run this
  // flag can only have been set by the ordinary Glow Up flow, and the ordinary abandon rule applies.
  const tutorialActive = useTutorialStore(selectExampleTourActive);

  useEffect(() => {
    // The example-tour rail owns the glow lifecycle (spec §10.2) — don't abandon on outside clicks.
    if (!step || tutorialActive) return;
    const handler = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (!target.closest('[data-glow-target]')) {
        setStep(null); // abandon
      }
    };
    // Use capture phase + slight delay so the button's own click fires first
    const timer = setTimeout(() => {
      document.addEventListener('click', handler, true);
    }, 100);
    return () => {
      clearTimeout(timer);
      document.removeEventListener('click', handler, true);
    };
  }, [step, setStep, tutorialActive]);
}
